import { useEffect, useMemo, useState } from "react";
import { DateTime, Settings } from "luxon";
import { ExtractRouteParams, useParams } from "react-router";
import { useHistory } from "react-router-dom";
import { LoadingInterstitial } from "swing-components";

import { useAuth } from "~components";
import { AUTH_URLS, createTimesheetEntryNewUrl, createTimesheetUrl } from "~pages";
import { replacePath } from "~utils";

import { TimesheetView } from "./components/TimesheetView";
import { useTimesheet } from "./hooks";
import { generateWeekId, getWeekRangeFromWeekId } from "./utils";

/**
 * Page | Timesheet
 *
 * URL: /more/timesheet/:weekId?
 *
 * Renders a list of TimeCard components, which represents the start time,
 * end time and lunch start/end time per request per day.
 *
 * It also renders a list of Paycode components, which represent a non-request
 * time entry generally representing sick time or training time.
 */
export function TimesheetPage() {
  /*** Hooks ***/
  // Represents the :weekId (e.g. 2025-01) in the URL
  const { weekId: weekIdParam } =
    useParams<ExtractRouteParams<typeof AUTH_URLS.timesheet, string>>();

  const { userInfo } = useAuth();
  const history = useHistory();

  /*** State ***/
  const [selectedDate, setSelectedDate] = useState(
    // If the weekId is given
    weekIdParam
      ? // Then default the selected date to be the start of the week
        getWeekRangeFromWeekId(weekIdParam).weekStart
      : // Otherwise store undefined to indicate that no weekId is selected
        // The useEffect below will handle setting this and updating the URL
        undefined,
  );

  /*** Effects ***/
  // On mount, if no `weekId` is in the URL, then set the `selectedDate` to the
  // current date and update the URL.
  useEffect(() => {
    if (!selectedDate) {
      setSelectedDate(DateTime.now());
      replacePath(createTimesheetUrl(generateWeekId(DateTime.now())));
    }
    // Only triggered on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On change of the selected date, update the URL to keep them in sync.
  useEffect(() => {
    replacePath(createTimesheetUrl(generateWeekId(selectedDate)));
  }, [selectedDate]);

  /*** Constants ***/
  const weekId = selectedDate ? generateWeekId(selectedDate) : undefined;

  /*** Queries ***/
  const {
    data: timesheetEntries,
    isLoading,
    isError,
  } = useTimesheet({ weekId, now: DateTime.now(), userState: userInfo.address?.state });

  /*** Constants #2 ***/
  // Determine the timezone of any school in the timesheet as that's the
  // best we can use to determine the timezone of the Sub.
  const timesheetTimezone = useMemo(
    () =>
      timesheetEntries?.filter((timesheet) => timesheet.type === "TIMECARD")[0]?.school.timeZone,
    [timesheetEntries],
  );

  /*** Effects #2 ***/
  /**
   * When the timesheetTimezone changes (due to timesheet data coming back)
   * Then set the default timezone for DateTime (Luxon)
   * And update the selectedDate to be in the same timezone as the timesheet.
   *
   * NOTE: This is important to make sure time and week comparisons are similar
   * between the `now` and `selectedDate` since the `now` is only instantiate
   * after the `Settings.defaultZone` is set.
   */
  useEffect(() => {
    if (!timesheetTimezone) return;

    if (timesheetTimezone) {
      Settings.defaultZone = timesheetTimezone;
    }

    if (selectedDate?.zoneName !== timesheetTimezone && weekIdParam) {
      setSelectedDate(getWeekRangeFromWeekId(weekIdParam).weekStart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetTimezone]);

  /*** Handlers ***/
  /** Triggered when sub changes the date via the DateFilterChip */
  function handleDateChange(selectedDate: string) {
    setSelectedDate(DateTime.fromISO(selectedDate));
    // Update the URL without causing a re-render
    replacePath(createTimesheetUrl(generateWeekId(DateTime.fromISO(selectedDate))));
  }

  /** Triggered when sub clicks the "New Card Entry" button */
  function handleNewCardEntry() {
    if (!weekId) return;
    history.push(createTimesheetEntryNewUrl(weekId));
  }

  /*** Render #1 - No Data Selected */
  if (!selectedDate || isLoading) {
    return (
      <div css={{ display: "flex", height: "100%", justifyContent: "center" }}>
        <LoadingInterstitial />
      </div>
    );
  }

  /*** Render #2***/
  return (
    <TimesheetView
      now={DateTime.now()}
      selectedDate={selectedDate}
      records={timesheetEntries}
      onDateChange={handleDateChange}
      onNewCardEntry={handleNewCardEntry}
      isLoading={isLoading}
      isError={isError}
    />
  );
}
